import type { AuthResponse, Role, UserResponse } from '@swi/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setUnauthorizedHandler, storeToken, storedToken } from '../api/client';
import * as api from '../api/endpoints';

interface AuthState {
  user: UserResponse | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<UserResponse>;
  register: (email: string, password: string, displayName: string) => Promise<UserResponse>;
  logout: () => void;
  isStaff: boolean;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const logout = useCallback(() => {
    storeToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(logout);
  }, [logout]);

  useEffect(() => {
    if (storedToken() === null) {
      setStatus('anonymous');
      return;
    }
    api
      .me()
      .then((current) => {
        setUser(current);
        setStatus('authenticated');
      })
      .catch(() => {
        logout();
      });
  }, [logout]);

  const accept = useCallback((response: AuthResponse): UserResponse => {
    storeToken(response.accessToken);
    setUser(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      login: async (email, password) => accept(await api.login({ email, password })),
      register: async (email, password, displayName) =>
        accept(await api.register({ email, password, displayName })),
      logout,
      isStaff: user?.role === 'OWNER_STAFF' || user?.role === 'ADMIN',
      hasRole: (...roles) => (user ? roles.includes(user.role) : false),
    }),
    [user, status, accept, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
